import { TimeInForceType } from '@/compiled_proto/com/celertech/orderrouting/api/enums/TimeInForceTypeProto';
import { submitLimitOrder } from '@/services/OrderService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import useLimitOrder from '@/utils/hooks/useLimitOrder';
import { UseOrderExecutionReturn } from '@/utils/hooks/useOrderExecution';
import useScreenSizes from '@/utils/hooks/useScreenSizes';
import useTotals from '@/utils/hooks/useTotals';
import { yupResolver } from '@hookform/resolvers/yup';
import BigNumber from 'bignumber.js';
import { useCallback, useEffect, useState } from 'react';
import { isTablet } from 'react-device-detect';
import { FormProvider, useForm } from 'react-hook-form';
import Loader from '../common/Loader';
import Tooltip from '../common/Tooltip';
import ExecutionInputController from '../inputs/ExecutionInputController';
import RHFNumberInput from '../inputs/RHFNumberInput';
import Select from '../inputs/Select';
import SlippageInput from '../inputs/SlippageInput';
import FormBody from './components/FormBody';
import FormCurrency from './components/FormCurrency';
import FormFooter from './components/FormFooter';
import FormOrderTypeSwitch from './components/FormOrderTypeSwitch';
import FormSubmit from './components/FormSubmit';
import Totals from './components/Totals';
import { LimitOrderFormInput, LimitOrderFormValues, limitOrderSchema } from './schema/limitOrderSchema';

const durationOptions: { label: keyof typeof TimeInForceType; value: TimeInForceType }[] = [
    { label: 'DAY', value: TimeInForceType.DAY },
    { label: 'IOC', value: TimeInForceType.IOC },
    { label: 'FOK', value: TimeInForceType.FOK },
    { label: 'GTC', value: TimeInForceType.GTC }
];

export const defaultValues: Partial<LimitOrderFormInput> = {
    limitPrice: null,
    quantity: null,
    duration: durationOptions[0],
    slippage: '0.00000'
};

interface LimitOrderFormProps extends UseOrderExecutionReturn {}

function LimitOrderForm(props: LimitOrderFormProps) {
    const {
        activePair,
        ccy1,
        ccy2,
        sideState,
        currencyState,
        orderBook,
        instrument,
        orderDecimals,
        minOrderSize,
        activeCurrencyConfig,
        isCcy2Order
    } = props;

    const lang = useLanguage();
    const screen = useScreenSizes();
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const [side, setSide] = sideState;
    const [currency, setCurrency] = currencyState;
    const [stateLoading, setStateLoading] = useState(false);

    const { asks, bids } = orderBook;
    const { pip_size, defaultSlippage } = instrument;
    const { increments } = activeCurrencyConfig;

    const form = useForm<LimitOrderFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(
            limitOrderSchema({
                min_order_size: minOrderSize,
                order_decimals: orderDecimals,
                currencyOut: currency,
                currencyPair: activePair?.show
            })
        )
    });

    const totals = useTotals({
        form,
        activePair,
        side,
        ccy1,
        ccy2,
        ccy2Order: isCcy2Order,
        bids,
        asks
    });

    const {
        register,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = form;

    const onSubmit = async (data: LimitOrderFormInput, e) => {
        const dataValidated = data as LimitOrderFormValues;
        const slippage = dataValidated.slippage
            ? BigNumber(dataValidated.slippage)
                  .dividedBy(Math.pow(10, pip_size || 0))
                  .toString()
            : '0';
        if (dataValidated.quantity && credentials) {
            await submitLimitOrder(
                {
                    securityId: activePair?.celer,
                    currencyOut: currency,
                    quantity: dataValidated.quantity,
                    limitPrice: dataValidated.limitPrice,
                    timeInForce: dataValidated.duration.value as TimeInForceType,
                    slippage,
                    side
                },
                credentials,
                orderBook.settlementDate,
                currentAccount
            );
        }
    };

    const onError = (errors) => console.error(errors);

    const { limitPriceTooltip, isRestingLimitOrder, resetDefault } = useLimitOrder(form, props, '');

    const reloadState = useCallback(() => {
        setStateLoading(true);
        resetDefault();
        setTimeout(() => setStateLoading(false), 300);
    }, [resetDefault, setStateLoading]);

    useDidUpdate(() => reloadState(), [currency]);
    useEffect(() => reloadState(), [activePair?.celer]);

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit, onError)} className="relative flex h-full w-full text-brand-text">
                <FormBody side={side}>
                    <FormOrderTypeSwitch initialValue={side} onChange={setSide} />
                    <FormCurrency {...props} currency={currency} setCurrency={setCurrency} />
                    {stateLoading ? (
                        <div className="mb-2">
                            <Loader />
                        </div>
                    ) : (
                        <div>
                            <Tooltip content={limitPriceTooltip || ''} className="">
                                <RHFNumberInput
                                    label={`${lang.orderForm.limitPrice} *`}
                                    placeholder={lang.orderForm.limitPrice}
                                    {...register('limitPrice')}
                                    disabled={!isRestingLimitOrder}
                                    className={{
                                        'text-brand-red': limitPriceTooltip,
                                        'text-brand-text': !limitPriceTooltip
                                    }}
                                    step={BigNumber(1)
                                        .dividedBy(Math.pow(10, pip_size || 0))
                                        .toNumber()}
                                    currency={activeCurrencyConfig.show}
                                />
                            </Tooltip>
                            {(screen.mobile || screen.tablet || isTablet) && (
                                <span className="text-brand-red italic">{limitPriceTooltip}</span>
                            )}
                        </div>
                    )}
                    <ExecutionInputController name="duration" label={`${lang.orderForm.duration} *`}>
                        <Select type="execution" options={durationOptions} />
                    </ExecutionInputController>
                    <RHFNumberInput
                        {...register('quantity')}
                        label={`${lang.orderForm.quantity} *`}
                        placeholder={lang.orderForm.quantity}
                        min={minOrderSize}
                        step={increments}
                        currency={activeCurrencyConfig.show}
                    />
                    <SlippageInput
                        {...register('slippage')}
                        label={lang.orderForm.slippage}
                        placeholder={lang.orderForm.slippage}
                        disabled={isRestingLimitOrder}
                        defaultSlippage={defaultSlippage}
                        step={0.1}
                    />
                    <Totals {...totals} />
                </FormBody>
                <FormFooter>
                    <FormSubmit
                        side={side}
                        isValid={isValid}
                        disabled={isSubmitting || !isValid || totals.disableSubmit}
                        noSettlementDate={!orderBook.settlementDate}
                    />
                </FormFooter>
            </form>
        </FormProvider>
    );
}

export default LimitOrderForm;
